<template>
  <PageVC>
    <div class="a-container">
      <TableCTA class="booking__table-cta"></TableCTA>
      <h1 class="a-page-title">Bordbestilling</h1>
      <form @submit="handleFormSubmit">
        <HUIFieldset class="booking__fieldset booking__fieldset--restaurant">
          <HUILegendWithBullets class="booking__fieldset-legend" centered>1</HUILegendWithBullets>
          <h2 class="booking__fieldset-heading">Hvilken restaurant vil du booke bord på?</h2>
          <HUISlider
            ref="restaurant-slider"
            key="restaurants-exist"
            v-if="restaurants"
            :initial-slide="initialRestaurantSlide"
            class="booking__slider a-container__offset"
          >
            <div class="booking__slide" v-for="obj in restaurants" :key="obj.slug">
              <HUIRadioButton
                :value="obj.slug"
                v-model="restaurantSlug"
                @change="handleRadioButtonChange"
              >
                {{obj.name}}
                <template slot="description">{{obj.address}}</template>
              </HUIRadioButton>
            </div>
          </HUISlider>
          <HUISlider key="restaurants-loading" v-else class="booking__slider a-container__offset">
            <HUILoadingView class="booking__spinner" />
          </HUISlider>
          <p
            class="booking__feedback"
            v-if="selectedRestaurant.name && !selectedRestaurant.has_table_booking"
          >Der kan ikke bestilles bord på restauranten.</p>
        </HUIFieldset>

        <HUIFieldset
          class="booking__fieldset booking__fieldset--guests"
          scroll-into-view-when-enabled
          :disabled="!(selectedRestaurant.has_table_booking && restaurantSlug)"
        >
          <HUILegendWithBullets class="booking__fieldset-legend" centered>2</HUILegendWithBullets>
          <h2 class="booking__fieldset-heading">Hvor mange gæster bliver i?</h2>
          <HUISlider
            :initial-slide="initialGuestsSlide"
            class="booking__slider a-container__offset"
          >
            <div
              class="booking__slide"
              v-for="n in (selectedRestaurant && selectedRestaurant.booking_email_threshold || 10)"
              :key="'guests-' + n"
            >
              <HUIRadioButton :value="n" v-model="guests" @change="handleRadioButtonChange">{{n}}</HUIRadioButton>
            </div>
            <div class="booking__slide">
              <HUIRadioButton
                :value="(selectedRestaurant && selectedRestaurant.booking_email_threshold || 10) + 1"
                v-model="guests"
                @change="handleRadioButtonChange"
              >{{(selectedRestaurant && selectedRestaurant.booking_email_threshold || 10) + 1}}+</HUIRadioButton>
            </div>
          </HUISlider>
          <p class="booking__feedback" v-if="showTenPlusWarning">
            For bordbestilling til mere end {{selectedRestaurant.booking_email_threshold}} personer skriv til
            <a
              :href="'mailto:' + selectedRestaurant.booking_email"
            >{{selectedRestaurant.booking_email}}</a>
          </p>
        </HUIFieldset>

        <HUIFieldset
          class="booking__fieldset booking__fieldset--day"
          scroll-into-view-when-enabled
          :disabled="!(selectedRestaurant.has_table_booking && restaurantSlug && guests && guests !== '10+')"
        >
          <HUILegendWithBullets class="booking__fieldset-legend" centered>3</HUILegendWithBullets>
          <h2 class="booking__fieldset-heading">Hvilken dag?</h2>
          <HUISlider
            key="days-exist"
            v-if="days && days.length"
            :initial-slide="initialDateSlide"
            class="booking__slider a-container__offset"
          >
            <div
              class="booking__slide"
              :class="{'booking__slide--day-in-the-past': obj.inThePast}"
              v-for="obj in visibleDays"
              :key="obj.timestamp"
            >
              <span v-if="obj.today" class="booking__slide-today-label" data-label="I dag"></span>
              <HUIRadioButton
                :disabled="obj.inThePast"
                :value="obj.formatted"
                v-model="date"
                @change="handleRadioButtonChange"
              >
                {{obj.name}}
                <template slot="description">{{obj.date}}/{{obj.month}}</template>
              </HUIRadioButton>
            </div>
            <div
              class="booking__slide booking__slide--show-more"
              v-if="visibleDays.length !== days.length"
            >
              <button
                class="booking__show-more-button"
                @click.prevent="daysPage = daysPage + 1"
              >Vis flere</button>
            </div>
          </HUISlider>
          <HUISlider key="days-loading" v-else class="booking__slider a-container__offset">
            <HUILoadingView class="booking__spinner" />
          </HUISlider>
        </HUIFieldset>

        <HUIFieldset
          class="booking__fieldset booking__fieldset--times"
          scroll-into-view-when-enabled
          :disabled="!(selectedRestaurant.has_table_booking && date && availableTimes && restaurantSlug && guests && guests !== '10+')"
        >
          <HUILegendWithBullets class="booking__fieldset-legend" centered>4</HUILegendWithBullets>
          <h2 class="booking__fieldset-heading">Hvad tid?</h2>
          <HUISlider class="booking__slider a-container__offset">
            <template v-if="availableTimes && availableTimes.length > 0">
              <div class="booking__slide" v-for="obj in availableTimes" :key="obj.time">
                <HUIRadioButton
                  :disabled="obj.disabled"
                  :value="obj.time"
                  v-model="time"
                  @change="handleRadioButtonChange"
                >{{obj.time}}</HUIRadioButton>
              </div>
            </template>
            <p class="booking__slider-placeholder" v-else>Vælg en dag</p>
            <HUILoadingView class="booking__spinner" v-if="loadingAvailableTimes" />
          </HUISlider>
        </HUIFieldset>

        <HUIFieldset class="booking__fieldset" scroll-into-view-when-enabled :disabled="!isValid">
          <HUIButton type="submit" theme="green" to="BOOKING_2">Videre til næste trin</HUIButton>
        </HUIFieldset>
      </form>
    </div>
  </PageVC>
</template>

<script>
import { mapState } from 'vuex'
import {
  HUILegendWithBullets,
  HUIRadioButton,
  HUISlider,
  HUILoadingView,
  HUIButton,
  HUIFieldset
} from '@/halifax-ui'
import PageVC from '@/containers/PageVC'
import flowStep from '@/mixins/flowStep'
import TableCTA from '@/containers/TableCTA'
import routes from '@/router/routes'

export default {
  mixins: [flowStep],
  components: {
    HUIButton,
    HUILegendWithBullets,
    HUIRadioButton,
    HUISlider,
    HUILoadingView,
    PageVC,
    HUIFieldset,
    TableCTA
  },
  data() {
    const slugFromQuery = this.$route.query.restaurant
    const { restaurant, guests, date, time } = JSON.parse(
      window.sessionStorage.getItem('booking/temp-booking') || '{}'
    )

    return {
      restaurantSlug: slugFromQuery || (restaurant && restaurant.slug) || null,
      guests,
      date,
      time,
      showTenPlusWarning: false,
      daysPage: 0,
      loadingAvailableTimes: false
    }
  },
  computed: {
    ...mapState('booking', {
      availableTimes: 'available_times',
      days: 'available_days'
    }),
    ...mapState('restaurant', {
      restaurants: 'restaurants'
    }),
    selectedRestaurant() {
      return this.restaurants && this.restaurantSlug
        ? this.restaurants.find(restaurant => restaurant.slug === this.restaurantSlug)
        : {}
    },
    visibleDays() {
      const daysPerPage = 14
      return this.days ? this.days.slice(0, daysPerPage * (this.daysPage + 1)) : null
    },
    initialDateSlide() {
      return this.visibleDays && this.date
        ? this.visibleDays.indexOf(this.visibleDays.find(obj => obj.formatted === this.date))
        : null
    },
    initialRestaurantSlide() {
      let index

      if (!this.restaurantSlug) {
        index = Infinity
      } else {
        index = this.restaurants ? this.restaurants.indexOf(this.selectedRestaurant) : null
      }

      return index
    },
    initialGuestsSlide() {
      return this.guests ? Math.max(this.guests - 1, 0) : 0
    }
  },
  watch: {
    date() {
      this.fetchAvailableTimes()
    },
    guests() {
      this.fetchAvailableTimes()
    },
    restaurants(newValue) {
      if (!this.restaurantSlug) {
        this.$nextTick(() => {
          this.$refs['restaurant-slider'].showSlide(0)
        })
      }
    },
    selectedRestaurant() {
      this.fetchAvailableTimes()
    }
  },
  created() {
    if (!this.days) {
      this.$store.dispatch('booking/COMPUTE_AVAILABLE_DAYS')
    }
    this.__setValidity(false)

    // We might have prefilled model values from sessionStorage, so fetch here aswell
    this.fetchAvailableTimes()
  },
  methods: {
    afterEnter() {
      if (this.restaurants && !this.restaurantSlug) {
        this.$nextTick(() => {
          this.$refs['restaurant-slider'].showSlide(0)
        })
      }
    },
    handleRadioButtonChange(event) {
      if (!this.selectedRestaurant.has_table_booking) {
        this.time = null
        this.date = null
        this.guests = null
      }
      if (this.guests > this.selectedRestaurant.booking_email_threshold) {
        this.showTenPlusWarning = true
        this.time = null
        this.date = null
      } else {
        this.showTenPlusWarning = false
      }
      this.__setValidity(this.restaurantSlug && this.date && this.guests && this.time)
    },
    async fetchAvailableTimes() {
      const restaurant = this.selectedRestaurant
      const { restaurantSlug, guests, date } = this

      if (restaurant.pos_id && restaurantSlug && guests && date) {
        this.loadingAvailableTimes = true
        this.time = null
        await this.$store.dispatch('booking/FETCH_AVAILABLE_TIMES', {
          restaurant,
          guests,
          date
        })
        if (!this.availableTimes.length) {
          // Nullify time again here to avoid race conditions
          this.time = null
        }
        this.loadingAvailableTimes = false
      }
    },
    handleFormSubmit(event) {
      this.$emit('subview-event', {
        type: 'step1-complete',
        data: {
          restaurant: this.selectedRestaurant,
          guests: this.guests,
          date: this.date,
          time: this.time
        }
      })
      event.preventDefault()
    }
  }
}
</script>

<style lang="scss">
</style>
